/* ==========================================================================
   Cart drawer upsells
   --------------------------------------------------------------------------
   Reads complementary products (set per-product in the Shopify Search &
   Discovery app) for whatever is currently in the cart, and renders them in
   the mini-cart above the subtotal. Falls back to a curated collection when a
   product has no complementary picks.

   Never modifies existing cart lines - it only ever adds new ones - so line
   item properties such as "Send on" and the gift card recipient flag are
   untouched.

   Config comes from the JSON block rendered by snippets/cart-upsells.liquid.
   ========================================================================== */
(function () {
  'use strict';

  var CONFIG_SELECTOR = '[data-cart-upsells-config]';
  var ROOT_SELECTOR = '[data-cart-upsells]';
  var MAX_SOURCE_PRODUCTS = 3; // how many cart items we ask for recommendations for

  var config = null;
  var rendering = false;
  var queued = false;

  /* ---------------------------------------------------------------- config */

  function getConfig() {
    if (config) return config;

    var el = document.querySelector(CONFIG_SELECTOR);
    if (!el) return null;

    try {
      config = JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }

    config.excludedTypes = config.excludedTypes || [];
    config.excludedHandles = config.excludedHandles || [];
    config.fallback = config.fallback || [];
    config.limit = config.limit || 4;

    return config;
  }

  /* --------------------------------------------------------------- helpers */

  function escapeHTML(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function money(cents) {
    var cfg = getConfig();

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && cfg.moneyFormat) {
      return window.Shopify.formatMoney(cents, cfg.moneyFormat);
    }

    return '$' + (Number(cents) / 100).toFixed(2);
  }

  function getJSON(url) {
    return fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  // Normalises both the recommendations API shape and the Liquid-rendered
  // fallback shape into one object.
  function normalize(product) {
    var variants = product.variants || [];
    var first = variants[0] || {};

    return {
      id: product.id,
      title: product.title,
      url: product.url || (product.handle ? '/products/' + product.handle : '#'),
      image: product.featured_image || product.image || null,
      price: typeof product.price === 'number' ? product.price : first.price,
      type: product.type || product.product_type || '',
      handle: product.handle || '',
      available: product.available !== false,
      variantId: product.variantId || first.id || null,
      variantCount:
        typeof product.variantCount === 'number'
          ? product.variantCount
          : variants.length || 1
    };
  }

  // Arrangements and gift cards are excluded because they collect required
  // fields (delivery date, recipient email) on the product page. A one-click
  // add has nowhere to ask for those, and the resulting line item would reach
  // fulfilment with no date attached.
  function eligible(item, cartProductIds) {
    var cfg = getConfig();

    if (!item.id || !item.variantId) return false;
    if (!item.available) return false;
    if (cartProductIds.indexOf(item.id) !== -1) return false;
    if (cfg.excludedTypes.indexOf(item.type) !== -1) return false;
    if (cfg.excludedHandles.indexOf(item.handle) !== -1) return false;

    return true;
  }

  /* ----------------------------------------------------------------- fetch */

  function recommendationsFor(productId) {
    var cfg = getConfig();

    return getJSON(
      '/recommendations/products.json?product_id=' +
        encodeURIComponent(productId) +
        '&intent=complementary&limit=' +
        cfg.limit
    ).then(function (data) {
      return (data && data.products) || [];
    });
  }

  /* ---------------------------------------------------------------- render */

  function cardHTML(item) {
    var cfg = getConfig();

    // Multi-variant products can't be added in one click without asking which
    // one, so we link to the product page instead.
    var action =
      item.variantCount > 1
        ? '<a class="cart-upsells__button" href="' +
          escapeHTML(item.url) +
          '">' +
          escapeHTML(cfg.chooseLabel) +
          '</a>'
        : '<button type="button" class="cart-upsells__button" data-upsell-add="' +
          escapeHTML(item.variantId) +
          '">' +
          escapeHTML(cfg.addLabel) +
          '</button>';

    var image = item.image
      ? '<a class="cart-upsells__image" href="' +
        escapeHTML(item.url) +
        '"><img src="' +
        escapeHTML(item.image) +
        '" alt="' +
        escapeHTML(item.title) +
        '" loading="lazy"></a>'
      : '';

    return (
      '<li class="cart-upsells__item">' +
      image +
      '<div class="cart-upsells__details">' +
      '<a class="cart-upsells__title" href="' +
      escapeHTML(item.url) +
      '">' +
      escapeHTML(item.title) +
      '</a>' +
      '<span class="cart-upsells__price">' +
      money(item.price) +
      '</span>' +
      '</div>' +
      action +
      '</li>'
    );
  }

  // There can be more than one container on a page - the header mini-cart on
  // desktop, and the cart page itself (which is all mobile shoppers get, since
  // the drawer doesn't exist there). Paint every one we find.
  function paint(items) {
    var cfg = getConfig();
    var roots = document.querySelectorAll(ROOT_SELECTOR);
    if (!roots.length) return;

    var html = items.length
      ? '<p class="cart-upsells__heading">' +
        escapeHTML(cfg.heading) +
        '</p>' +
        '<ul class="cart-upsells__list">' +
        items.map(cardHTML).join('') +
        '</ul>'
      : '';

    Array.prototype.forEach.call(roots, function (root) {
      root.innerHTML = html;
      root.hidden = !items.length;
    });
  }

  function render() {
    var cfg = getConfig();
    if (!cfg) return Promise.resolve();

    if (rendering) {
      queued = true;
      return Promise.resolve();
    }
    rendering = true;

    return getJSON('/cart.js')
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) {
          paint([]);
          return null;
        }

        var cartProductIds = [];
        cart.items.forEach(function (line) {
          if (cartProductIds.indexOf(line.product_id) === -1) {
            cartProductIds.push(line.product_id);
          }
        });

        var sources = cartProductIds.slice(0, MAX_SOURCE_PRODUCTS);

        return Promise.all(sources.map(recommendationsFor)).then(function (sets) {
          var seen = [];
          var picks = [];

          function collect(list) {
            list.forEach(function (raw) {
              if (picks.length >= cfg.limit) return;

              var item = normalize(raw);
              if (seen.indexOf(item.id) !== -1) return;
              if (!eligible(item, cartProductIds)) return;

              seen.push(item.id);
              picks.push(item);
            });
          }

          sets.forEach(collect);

          // Nothing tagged in Search & Discovery for these products - fall
          // back to the curated add-ons collection.
          if (!picks.length) {
            collect(cfg.fallback);
          }

          paint(picks);
          return null;
        });
      })
      .then(function () {
        rendering = false;
        if (queued) {
          queued = false;
          render();
        }
      })
      .catch(function () {
        rendering = false;
      });
  }

  /* ------------------------------------------------------------------- add */

  // Turbo owns the mini-cart markup and rebuilds it from its own AJAX flow.
  // Rather than duplicate that, we hand the add off to the theme by creating a
  // throwaway form and clicking its .ajax-submit button, which Turbo listens
  // for via a delegated handler. The drawer then refreshes exactly as it does
  // for a normal add to cart.
  //
  // The form goes on document.body, not inside the mini-cart - the mini-cart is
  // itself a form, and browsers strip nested forms.
  function addVariant(variantId) {
    var usesAjaxCart =
      window.PXUTheme &&
      window.PXUTheme.theme_settings &&
      window.PXUTheme.theme_settings.cart_action === 'ajax';

    var form = document.createElement('form');
    form.setAttribute('action', '/cart/add');
    form.setAttribute('method', 'post');
    form.setAttribute('enctype', 'multipart/form-data');
    form.style.display = 'none';
    form.innerHTML =
      '<input type="hidden" name="id" value="' + escapeHTML(variantId) + '">' +
      '<input type="hidden" name="quantity" value="1">' +
      '<button type="button" class="add_to_cart ajax-submit"><span class="text"></span></button>';

    document.body.appendChild(form);

    if (usesAjaxCart) {
      form.querySelector('.ajax-submit').click();
      window.setTimeout(function () {
        if (form.parentNode) form.parentNode.removeChild(form);
      }, 5000);
    } else {
      // No AJAX cart configured - fall back to a normal post and page load.
      form.submit();
    }
  }

  /* -------------------------------------------------------------- listeners */

  document.addEventListener('click', function (event) {
    var button = event.target.closest
      ? event.target.closest('[data-upsell-add]')
      : null;
    if (!button) return;

    event.preventDefault();
    button.disabled = true;

    addVariant(button.getAttribute('data-upsell-add'));
  });

  // Turbo does all of its cart work through jQuery, so ajaxComplete is a
  // reliable, non-invasive signal that the cart changed. Our own requests use
  // fetch(), so they don't retrigger this.
  function bindCartRefresh() {
    if (!window.jQuery) return false;

    window.jQuery(document).ajaxComplete(function (event, xhr, settings) {
      var url = (settings && settings.url) || '';
      if (/\/cart(\.js|\/(add|change|update|clear)\.js)/.test(url)) {
        render();
      }
    });

    return true;
  }

  function init() {
    if (!document.querySelector(ROOT_SELECTOR)) return;

    if (!bindCartRefresh()) {
      // jQuery loads deferred; try again shortly.
      window.setTimeout(bindCartRefresh, 1000);
    }

    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();